import { useState, useMemo, useContext, useEffect } from 'react'
import { resourcesAdapter } from '../../adapter'
import util from '../../helpers/util.helper'
import useDataClient from '../../hooks/useDataClient'
import { AppContext, DashboardContext, DocumentationContext } from '../../context';
import { AppModulesType, ResourceType, ResourceVersionArtifactType } from '../../types';
import { GAHelper } from '../../helpers/ga.helper';
import TextContainer from './TextContainer';
import useUserStorage from '../../hooks/useUserStorage';
import { LINKS_ARTIFACT, REFDOC_ARTIFACT, SWAGGER_ARTIFACT } from '../../config';
import ZipContainer from './ZipContainer';
import DownloadFileContainer from './DownloadFileContainer';
import DocDisplayPage from '../helpDocs/DocDisplayPage';
import SwaggerUI from 'swagger-ui-react';
import ExcelContainer from './ExcelContainer';

interface ArtifactContainerProps {
  appModule: AppModulesType,
  resource: ResourceType,
  artifact: ResourceVersionArtifactType,
}

export default function ArtifactContainer({ resource, artifact, appModule }: ArtifactContainerProps) {
  const artifactCacheName = resource.name + artifact.id
  const appContext = useContext(AppContext)
  const dashboardContext = useContext(DashboardContext)
  const documentationContext = useContext(DocumentationContext)
  const artifactInCache = useUserStorage<string>(artifactCacheName, 'session')
  const mime = useMemo(() => util.getFileExtension(artifact.name), [artifact])
  const [file, setFile] = useState<Blob | null>(null)
  const url = useMemo<string>(() => {
    if (file && !['other', 'text'].includes(mime.type)) {
      return URL.createObjectURL(new Blob([file], { type: `${mime.type}/${mime.extension}` }))
    }
    return ''
  }, [mime, file])
  const fileClient = useDataClient<void>({ appContext })

  useEffect(() => {
    LINKS_ARTIFACT.find(i => artifact.type.startsWith(i)) ? setFile(null) :
      fileClient.executeRequest(resourcesAdapter.downloadResource(resource.idRepository, artifact.id, setFile))
  }, [resource.idRepository, artifact])

  useEffect(() => {
    if (!artifactInCache.data) {
      artifactInCache.setData(artifactCacheName)
      GAHelper.setArtifactView(appModule, resource, dashboardContext?.appData.afore, dashboardContext?.appData.user)
    }
    if (!file && !documentationContext?.referencedDoc?.fullScreen) {
      documentationContext?.setReferencedDoc({
        title: resource.label,
        reference: artifact.path || ''
      })
    }
  }, [file])

  if (!file) {
    console.log(artifact)
    switch (true) {
      case artifact.type.startsWith(REFDOC_ARTIFACT):
        return <section className='section' style={{ height: 'calc(100vh - 5rem)' }}>
          <DocDisplayPage />
        </section>
      case artifact.type.startsWith(SWAGGER_ARTIFACT):
        return <SwaggerUI
          url={artifact.path}
          defaultModelsExpandDepth={3}
          onComplete={(sys) => {
            let spec = sys.specSelectors.specJson().toJS();
            spec.info.summary = ''
            sys.specActions.updateJsonSpec(spec);
          }}
        />
      default:
        return <small>{artifact.name}</small>
    }
  }
  switch (mime.type) {
    case 'application':
      return <iframe className='mobile-doc-file' title={artifact.name} src={url} />
    case 'zip':
      return <ZipContainer file={file} filename={artifact.name} />
    case 'excel':
      return <ExcelContainer file={file} filename={artifact.name} fileExtension={mime.extension} />
    case 'text':
    case 'xml':
      return <div className='mobile-doc-file'>
        <TextContainer filename={artifact.name} file={file} fileExtension={mime.extension} />
      </div>
    case 'video':
      return <video className='mobile-doc-file' controls>
        <source src={url} />
        Your browser does not support the video tag.
      </video>;
    case 'image':
      return <div className='d-flex-center'><img src={url} alt={artifact.name} style={{ maxWidth: '100%', margin: 'auto' }} /></div>
    default:
      return <DownloadFileContainer file={file} artifact={artifact} />
  }
}
